/*
 * FXRatesView.tsx — S65: FX Rate Management
 * APIs: GET/POST /api/v1/fx/*
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';

/* ── Interfaces ── */
interface CurrencyPair {
  from_currency: string;
  to_currency: string;
  latest_rate_date: string | null;
  count: number;
}

interface RateHistoryEntry {
  date: string;
  rate: number;
}

interface StalenessInfo {
  ok: boolean;
  message?: string;
  stale_pairs?: string[];
}

interface FetchLogEntry {
  source: string;
  target_date: string;
  status: string;
  pairs_updated: number;
  duration_ms: number;
}

/* ── Helpers ── */
const statusBadge = (s: string) => {
  const colors: Record<string, string> = {
    SUCCESS: '#16a34a', COMPLETED: '#16a34a',
    FAILED: '#dc2626', ERROR: '#dc2626',
    RUNNING: '#2563eb', PENDING: '#ca8a04',
  };
  return (
    <span style={{
      background: colors[s?.toUpperCase()] || '#6b7280', color: 'var(--text-primary)', fontSize: 11,
      padding: '2px 8px', borderRadius: 4, fontWeight: 600,
    }}>{s}</span>
  );
};

/* ── Component ── */
export default function FXRatesView() {
  // State
  const [pairs, setPairs] = useState<CurrencyPair[]>([]);
  const [staleness, setStaleness] = useState<StalenessInfo | null>(null);
  const [selectedPair, setSelectedPair] = useState<{ from: string; to: string } | null>(null);
  const [history, setHistory] = useState<RateHistoryEntry[]>([]);
  const [fetchLog, setFetchLog] = useState<FetchLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Manual entry form
  const [form, setForm] = useState({
    from_currency: '',
    to_currency: '',
    rate_date: '',
    rate_type: 'CLOSING',
    rate: '',
  });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  /* ── Data loaders ── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pairsRes, stalenessRes, logRes] = await Promise.all([
        authFetch(`${API_V1}/fx/pairs`),
        authFetch(`${API_V1}/fx/staleness`),
        authFetch(`${API_V1}/fx/fetch-log?limit=10`),
      ]);
      if (pairsRes.ok) {
        const data = await pairsRes.json();
        setPairs(data.pairs || data || []);
      }
      if (stalenessRes.ok) {
        const data = await stalenessRes.json();
        setStaleness(data);
      }
      if (logRes.ok) {
        const data = await logRes.json();
        setFetchLog(data.logs || data.items || data || []);
      }
    } catch (e: any) {
      showMessage('error', e.message || 'Failed to load FX data.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── Load history for selected pair ── */
  const loadHistory = useCallback(async (from: string, to: string) => {
    try {
      const res = await authFetch(`${API_V1}/fx/history?from_currency=${encodeURIComponent(from)}&to_currency=${encodeURIComponent(to)}`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.rates || data.history || data || []);
      } else {
        setHistory([]);
      }
    } catch {
      setHistory([]);
    }
  }, []);

  const handleSelectPair = (from: string, to: string) => {
    setSelectedPair({ from, to });
    loadHistory(from, to);
  };

  /* ── Fetch latest rates ── */
  const handleFetchLatest = async () => {
    setFetching(true);
    try {
      const res = await authFetch(`${API_V1}/fx/fetch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        showMessage('success', data.message || 'Rates fetched successfully.');
        loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        showMessage('error', err.detail || `Fetch failed (${res.status}).`);
      }
    } catch (e: any) {
      showMessage('error', e.message || 'Network error fetching rates.');
    }
    setFetching(false);
  };

  /* ── Manual rate submission ── */
  const handleSubmitRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.from_currency || !form.to_currency || !form.rate_date || !form.rate) {
      showMessage('error', 'All fields are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_V1}/fx/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_currency: form.from_currency.toUpperCase(),
          to_currency: form.to_currency.toUpperCase(),
          rate_date: form.rate_date,
          rate_type: form.rate_type,
          rate: parseFloat(form.rate),
        }),
      });
      if (res.ok) {
        showMessage('success', 'Rate saved successfully.');
        setForm({ from_currency: '', to_currency: '', rate_date: '', rate_type: 'CLOSING', rate: '' });
        loadAll();
        if (selectedPair) loadHistory(selectedPair.from, selectedPair.to);
      } else {
        const err = await res.json().catch(() => ({}));
        showMessage('error', err.detail || `Save failed (${res.status}).`);
      }
    } catch (e: any) {
      showMessage('error', e.message || 'Network error saving rate.');
    }
    setSubmitting(false);
  };

  /* ── Render ── */
  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: '#94a3b8' }}>Loading FX rate data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
          FX Rate Management
        </h2>
        <button
          onClick={handleFetchLatest}
          disabled={fetching}
          style={{
            ...btnStyle,
            background: 'var(--navy-700)',
            opacity: fetching ? 0.6 : 1,
          }}
        >
          {fetching ? 'Fetching...' : 'Fetch Latest Rates'}
        </button>
      </div>

      {/* ── Message banner ── */}
      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: message.type === 'success' ? '#14532d' : '#7f1d1d',
          color: message.type === 'success' ? 'var(--positive)' : '#fca5a5',
          border: `1px solid ${message.type === 'success' ? '#16a34a' : '#dc2626'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* ── Staleness Banner ── */}
      {staleness && !staleness.ok && (
        <div style={{
          padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: 'var(--warning-bg)', color: 'var(--warning)', border: '1px solid #d97706',
        }}>
          {staleness.message || 'Some FX rates are stale and may need updating.'}
          {staleness.stale_pairs && staleness.stale_pairs.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#fcd34d' }}>
              Stale pairs: {staleness.stale_pairs.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* ── Layout: left (pairs + history) / right (form) ── */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* ── Currency Pairs Table ── */}
        <div style={{ flex: '1 1 500px' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 20, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              Currency Pairs
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['From', 'To', 'Latest Rate Date', 'Count'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pairs.map((p, idx) => {
                  const isSelected = selectedPair?.from === p.from_currency && selectedPair?.to === p.to_currency;
                  return (
                    <tr
                      key={idx}
                      onClick={() => handleSelectPair(p.from_currency, p.to_currency)}
                      style={{
                        borderBottom: '1px solid #1e293b',
                        cursor: 'pointer',
                        background: isSelected ? '#334155' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '6px 10px', color: 'var(--text-primary)', fontWeight: 600 }}>{p.from_currency}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--text-primary)', fontWeight: 600 }}>{p.to_currency}</td>
                      <td style={{ padding: '6px 10px', color: '#94a3b8', fontSize: 12 }}>
                        {p.latest_rate_date || '-'}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--text-primary)' }}>{p.count}</td>
                    </tr>
                  );
                })}
                {pairs.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                      No currency pairs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Rate History (for selected pair) ── */}
          {selectedPair && (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                Rate History: {selectedPair.from} / {selectedPair.to}
              </h3>
              {history.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: 13 }}>No rate history available for this pair.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', color: '#94a3b8', fontWeight: 600 }}>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                        <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{h.date}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                          {typeof h.rate === 'number' ? h.rate.toFixed(6) : h.rate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* ── Manual Entry Form ── */}
        <div style={{ flex: '0 1 360px' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              Manual Rate Entry
            </h3>
            <form onSubmit={handleSubmitRate} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={labelStyle}>From Currency</label>
                <input
                  type="text"
                  value={form.from_currency}
                  onChange={e => setForm(f => ({ ...f, from_currency: e.target.value }))}
                  placeholder="USD"
                  maxLength={10}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>To Currency</label>
                <input
                  type="text"
                  value={form.to_currency}
                  onChange={e => setForm(f => ({ ...f, to_currency: e.target.value }))}
                  placeholder="SAR"
                  maxLength={10}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Rate Date</label>
                <input
                  type="text"
                  value={form.rate_date}
                  onChange={e => setForm(f => ({ ...f, rate_date: e.target.value }))}
                  placeholder="2025-01-15"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Rate Type</label>
                <select
                  value={form.rate_type}
                  onChange={e => setForm(f => ({ ...f, rate_type: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="CLOSING">CLOSING</option>
                  <option value="AVERAGE">AVERAGE</option>
                  <option value="HISTORICAL">HISTORICAL</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Rate</label>
                <input
                  type="number"
                  step="any"
                  value={form.rate}
                  onChange={e => setForm(f => ({ ...f, rate: e.target.value }))}
                  placeholder="3.7500"
                  style={inputStyle}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  ...btnStyle,
                  background: 'var(--positive)',
                  opacity: submitting ? 0.6 : 1,
                  marginTop: 8,
                  width: '100%',
                }}
              >
                {submitting ? 'Saving...' : 'Save Rate'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* ── Fetch Log ── */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
          Fetch Log
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155' }}>
              {['Source', 'Target Date', 'Status', 'Pairs Updated', 'Duration (ms)'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fetchLog.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{row.source || '-'}</td>
                <td style={{ padding: '6px 10px', color: '#94a3b8', fontSize: 12 }}>{row.target_date || '-'}</td>
                <td style={{ padding: '6px 10px' }}>{statusBadge(row.status)}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--text-primary)' }}>{row.pairs_updated ?? '-'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--text-primary)' }}>{row.duration_ms ?? '-'}</td>
              </tr>
            ))}
            {fetchLog.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                  No fetch log entries.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Shared inline styles ── */
const labelStyle: React.CSSProperties = {
  fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4, fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-default)',
  borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
};

const btnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer',
  fontWeight: 600, fontSize: 13, color: 'var(--text-primary)',
};
